/**
 * JourneyScreen.tsx — the shared single-question flow layout.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: every journey step follows the same anatomy — a slim top progress rail
 * (with an optional back control and step count), the question content, then
 * an action row holding the step-forward CTA. Centralising it keeps the
 * one-question-per-screen rhythm identical across the whole flow.
 *
 * THE ACTION ROW IS STICKY ON MOBILE AGAIN (CTO, 2026-09-24 — "on mobile,
 * having to scroll is normal with the button fixed towards the bottom if there
 * is a CTA"). This REVERSES the 2026-07-21 "no longer sticky — the CTA scrolls
 * with the content" decision below 880px only: `ActionRow` (JourneyUi.tsx)
 * wears `.gps-jux-action-row`, which tokens.css pins to the bottom of the
 * viewport on a phone over a real white surface. On a desktop it is still the
 * inline row in normal flow. A screen with no action row — the single-select
 * questions, which advance the moment an answer is tapped — is unchanged.
 *
 * `footer` (2026-09-11) is secondary content below the action row — today the
 * in-journey call-back offer. It lives INSIDE the column on purpose, as the
 * screen's closing line (ResultScreen's phone bar reserves its space at the
 * very end of the page — the app shell's padding, after the legal footer — so
 * nothing here, or after it, can end up under that bar). It is opt-in per
 * screen, which is how the stop screens —
 * they carry the call-back form inline already — keep it off.
 *
 * Width: the classic question column shared by every journey stage — 32rem
 * below 880px and 40rem at >=880px (CTO 2026-09-24: a wider centre column on
 * desktop). Inline styles cannot carry a media query, so the width is the
 * `--gps-journey-col` custom property that `.gps-journey-root` sets per
 * breakpoint (tokens.css, L1); outside a journey root it falls back to 32rem.
 * `wide` (2026-09-23, CTO — the side-by-side Result screen) opens the column to
 * 64rem at ≥880px and is IDENTICAL to the classic column below that, via the
 * `.gps-journey-wide` media query (inline styles cannot carry one). It is used
 * ONLY by the Result screen. ⚠️ It is a WIDTH, never a transform: the Result
 * screen's "See my plan" bar is `position: fixed`, and any transformed ancestor
 * (`.gps-plan-breakout`'s translateX, say) would re-anchor it to that ancestor
 * instead of the viewport — which is why this variant exists at all.
 *
 * `className` (2026-09-25) is a paint hook on the column and nothing else: the
 * two question surfaces (QuestionFlow's question screen and Step 0) pass
 * `gps-jux-question`, which tokens.css (P12, "question glow") paints with a
 * soft peach bloom behind the heading and a halo round the enabled Continue —
 * a background and a box-shadow, never a position, transform or filter (the
 * call-back dialog in `footer` is a non-portaled `position: fixed` DialogShell).
 * Unset, the column renders exactly as before, with no class at all.
 */
import type { ReactNode } from 'react';
import { ActionRow, JourneyAppBar } from './JourneyUi';

export function JourneyScreen({
  onBack,
  percent,
  step,
  children,
  action,
  footer,
  wide = false,
  className,
}: {
  onBack?: () => void;
  percent?: number;
  step?: string;
  children: ReactNode;
  action?: ReactNode;
  /** Secondary content below the action row, inside the column (see header). */
  footer?: ReactNode;
  /** 64rem at ≥880px, 32rem below — the Result screen only (see header). */
  wide?: boolean;
  /** A paint hook on the column (see header) — `gps-jux-question` today. */
  className?: string;
}) {
  const showBar = typeof percent === 'number' || Boolean(onBack) || Boolean(step);
  return (
    <div
      className={[wide ? 'gps-journey-wide' : null, className].filter(Boolean).join(' ') || undefined}
      style={wide ? undefined : { maxWidth: 'var(--gps-journey-col, 32rem)', margin: '0 auto' }}
    >
      {showBar ? <JourneyAppBar onBack={onBack} percent={percent} step={step} /> : null}
      {/* JourneyAppBar carries no bottom margin, so the classic 16px rhythm
          lives here. */}
      <div style={{ marginTop: showBar ? 'var(--gps-s-4)' : undefined }}>{children}</div>
      {action ? <ActionRow>{action}</ActionRow> : null}
      {footer ?? null}
    </div>
  );
}
